import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, X, Lock, Plus } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import JobCard, { Listing, pseudoDistance } from '@/components/JobCard';
import Seo from '@/components/Seo';
import BrowseState, { FeedState } from '@/components/BrowseState';
import { fetchListingsResult, fetchPurchasedIds, MAX_PAGE_SIZE, deriveFeedState } from '@/lib/marketplace';
import { useOverlayLock } from '@/lib/overlay';
import { TRADES, US_STATES, tradeLabel } from '@/data/constants';
import PostPurchaseBanner from '@/components/PostPurchaseBanner';
import OnboardingTour from '@/components/OnboardingTour';
import { WelcomeTicket, readWelcomeTicket, clearWelcomeTicket, tourSeen } from '@/lib/activation';



const SCROLL_KEY = 'mkt_jobs_scroll';

const Jobs: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [jobs, setJobs] = useState<Listing[]>([]);
  const [purchased, setPurchased] = useState<string[]>([]);
  const [feed, setFeed] = useState<FeedState>('loading');
  const [retryAfter, setRetryAfter] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const restored = useRef(false);

  // ---------------------------------------------------------------------
  // POST-PAYMENT WELCOME. The return page writes a ticket after the verified
  // webhook activated the credits, so this page can confirm the purchase and
  // open the first-buyer tour exactly once.
  // ---------------------------------------------------------------------
  const [ticket, setTicket] = useState<WelcomeTicket | null>(null);
  const [tour, setTour] = useState(false);

  useEffect(() => {
    const found = readWelcomeTicket();
    if (!found) return;
    setTicket(found);
    if (!tourSeen()) {
      // Small delay so the page paints behind the walkthrough first.
      const id = window.setTimeout(() => setTour(true), 600);
      return () => window.clearTimeout(id);
    }
  }, []);


  // The mobile filter sheet is a full-screen overlay: register it so the
  // floating Start Now / WhatsApp stack steps aside while it is open.
  useOverlayLock(showFilters);


  const trade = params.get('trade') || '';
  const state = params.get('state') || '';
  const type = params.get('type') || '';
  const radius = params.get('radius') || '';
  const minBudget = params.get('budget') || '';

  const hasFilters = !!(trade || state || type || radius || minBudget);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  /**
   * Load page 0. Never collapses a failure into an empty list — the feed state
   * carries the real verdict so the UI can tell the user what happened.
   */
  const load = useCallback(async () => {
    setFeed('loading');
    const res = await fetchListingsResult({ trade, state, type, limit: MAX_PAGE_SIZE, offset: 0 });
    if (res.status !== 'ok') {
      setJobs([]);
      setRetryAfter(res.retryAfter);
      setFeed(res.status);
      return;
    }
    setJobs(res.listings);
    setOffset(res.listings.length);
    setHasMore(res.listings.length >= MAX_PAGE_SIZE);
    setFeed(res.listings.length === 0 ? 'empty' : 'ok');
  }, [trade, state, type]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const res = await fetchListingsResult({ trade, state, type, limit: MAX_PAGE_SIZE, offset });
    if (res.status === 'ok') {
      setJobs((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...res.listings.filter((l) => !seen.has(l.id))];
      });
      setOffset((o) => o + res.listings.length);
      setHasMore(res.listings.length >= MAX_PAGE_SIZE);
    } else {
      setRetryAfter(res.retryAfter);
      setFeed(res.status);
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchPurchasedIds(profile?.id).then(setPurchased);
  }, [profile?.id]);

  // Returning from a job detail page keeps the results and the scroll position.
  useEffect(() => {
    if (feed !== 'ok' || restored.current) return;
    const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
    if (saved > 0) window.scrollTo(0, saved);
    restored.current = true;
  }, [feed]);

  /**
   * Client-side refinement ONLY.
   * NOTE: city/ZIP are masked (null) in the public feed by design, so no filter
   * here may ever touch j.city — doing so would drop every listing.
   */
  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (radius && pseudoDistance(j.id) > Number(radius)) return false;
      if (minBudget) {
        const num = Number((j.budget_range || '').replace(/[^0-9]/g, '').slice(0, 6) || 0);
        // A listing with no budget range must not be hidden by a budget filter.
        if (j.budget_range && num < Number(minBudget)) return false;
      }
      return true;
    });
  }, [jobs, radius, minBudget]);

  const clear = () => setParams(new URLSearchParams(), { replace: true });

  const onUnlock = (job: Listing) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(path(`/jobs/${job.slug}`));
  };

  const selectCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-slate-900 focus:outline-none';
  const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

  const filterPanel = (
    <div className="grid gap-4">
      <div>
        <label className={labelCls}>{t('jobs.filterTrade')}</label>
        <select className={selectCls} value={trade} onChange={(e) => setParam('trade', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {TRADES.map((tr) => (
            <option key={tr.slug} value={tr.slug}>{tradeLabel(tr.slug, lang)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>{t('jobs.filterState')}</label>
        <select className={selectCls} value={state} onChange={(e) => setParam('state', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      {/* City is masked until a lead is claimed, so there is no city filter to
          offer here. Saying so plainly is better than a box that cannot match. */}
      <p className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs font-medium text-slate-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('jobs.cityHidden')}
      </p>
      <div>
        <label className={labelCls}>{t('jobs.filterRadius')}</label>
        <select className={selectCls} value={radius} onChange={(e) => setParam('radius', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {[10, 25, 50, 100].map((r) => (
            <option key={r} value={r}>{r} {t('jobs.miles')}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>{t('jobs.filterBudget')}</label>
        <select className={selectCls} value={minBudget} onChange={(e) => setParam('budget', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {[5000, 15000, 30000, 60000].map((b) => (
            <option key={b} value={b}>${b.toLocaleString()}+</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>{t('jobs.filterType')}</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: '', l: t('common.all') },
            { v: 'residential', l: t('common.residential') },
            { v: 'commercial', l: t('common.commercial') },
          ].map((o) => (
            <button
              key={o.v || 'all'}
              onClick={() => setParam('type', o.v)}
              className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                type === o.v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>
      <button onClick={clear} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
        {t('common.clear')}
      </button>
    </div>
  );

  // 'empty' ONLY when the request genuinely succeeded with zero rows.
  // deriveFeedState is shared with the permanent regression suite, so the logic
  // under test is literally the logic that renders.
  const shown: FeedState =
    feed === 'loading' || feed === 'empty'
      ? feed
      : deriveFeedState(feed, filtered.length);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Seo
        title={trade ? `${tradeLabel(trade, lang)} ${lang === 'es' ? 'trabajos' : 'jobs'}` : t('jobs.title')}
        description={t('jobs.subtitle')}
      />
      {/*
        POST-PURCHASE LANDING. This is the primary automatic destination after a
        successful payment, because this is where credits get spent. The banner
        confirms the purchase and the balance; the tour explains the next step.
      */}
      {ticket && (
        <PostPurchaseBanner
          ticket={ticket}
          onProjects
          onDismiss={() => {
            clearWelcomeTicket();
            setTicket(null);
          }}
          onReplayTour={() => setTour(true)}
        />
      )}

      <OnboardingTour
        open={tour}
        onClose={() => setTour(false)}
        plan={ticket?.plan}
        credits={ticket?.credits}
        jobsHref={path('/jobs')}
      />

      <div className="flex flex-wrap items-end justify-between gap-4">

        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('jobs.title')}</h1>
          <p className="mt-2 max-w-2xl text-slate-500">{t('jobs.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowFilters(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 lg:hidden"
        >
          <SlidersHorizontal className="h-4 w-4" /> {t('common.filters')}
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('common.filters')}</h2>
            {filterPanel}
          </div>
        </aside>

        <div>
          <p className="mb-4 text-sm font-semibold text-slate-500">
            {feed === 'loading' ? t('jobs.loading') : `${filtered.length} ${t('jobs.results')}`}
          </p>

          {shown !== 'ok' ? (
            <BrowseState
              state={shown}
              retryAfter={retryAfter}
              onRetry={load}
              onClearFilters={clear}
              hasFilters={hasFilters}
            />
          ) : (
            <>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((j) => (
                  <JobCard key={j.id} job={j} unlocked={purchased.includes(j.id)} onUnlock={onUnlock} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <Plus className="h-4 w-4" />
                    {loadingMore ? t('common.loading') : t('common.viewAll')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[90] bg-slate-900/50 lg:hidden" onClick={() => setShowFilters(false)}>
          <div
            className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-extrabold text-slate-900">{t('common.filters')}</h2>
              <button onClick={() => setShowFilters(false)} className="rounded-lg p-2 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            {filterPanel}
            <button
              onClick={() => setShowFilters(false)}
              className="mt-4 w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-bold text-white"
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
